import { useTranslation } from 'react-i18next';
import { Select, MenuItem } from '@mui/material';
import { PriceReadModel } from '@client/api';
import { toPriceString } from '@client/toolkit';

interface AgreementSpaceProductPriceSelectProps {
  value: string;
  prices: PriceReadModel[];
  onChange: (priceId: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export function AgreementSpaceProductPriceSelect({
  value,
  prices,
  onChange,
  isLoading = false,
  disabled = false,
  className,
}: AgreementSpaceProductPriceSelectProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tType } = useTranslation('type');

  const getBillingTypeLabel = (price: PriceReadModel): string => {
    return price.recurring ? tType('BillingType.Monthly') : tType('BillingType.OneTime');
  };

  const handleChange = (event: { target: { value: unknown } }) => {
    const selectedPriceId = event.target.value as string;
    if (selectedPriceId) {
      onChange(selectedPriceId);
    }
  };

  const isDisabled = isLoading || disabled || prices.length === 0;

  const renderValue = (selected: unknown) => {
    if (disabled) {
      return <span className="!opacity-50">– {tGlobal('agreementSpace.selectProductFirst')} –</span>;
    }
    if (!selected) return '';
    const price = prices.find((p) => p.id === selected);
    if (!price) return '';
    const billingType = getBillingTypeLabel(price);
    return `${toPriceString(price)} (${billingType})`;
  };

  return (
    <Select
      size="small"
      className={`w-full !text-primary border border-border ${isDisabled ? 'cursor-not-allowed' : ''} ${className || ''}`}
      value={value}
      onChange={handleChange}
      renderValue={renderValue}
      displayEmpty
      disabled={isDisabled}
      sx={{
        fontSize: '12px',
        '&.Mui-disabled': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
          opacity: 1,
          color: 'inherit',
          '-webkit-text-fill-color': 'inherit',
          '& .MuiSelect-select': {
            cursor: 'not-allowed',
            '-webkit-text-fill-color': 'inherit',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            cursor: 'not-allowed',
            borderColor: 'rgb(38 38 38)', // neutral-800
          },
          '& .MuiSvgIcon-root': {
            cursor: 'not-allowed',
            color: 'inherit',
          },
        },
      }}
    >
      {prices.length === 0 ? (
        <MenuItem value="" disabled>
          {tCommon('noOptions')}
        </MenuItem>
      ) : (
        prices.map((price) => {
          const billingType = getBillingTypeLabel(price);
          return (
            <MenuItem key={price.id} value={price.id}>
              {toPriceString(price)}
              <span className="ml-2 text-secondary">({billingType})</span>
            </MenuItem>
          );
        })
      )}
    </Select>
  );
}
